/**
 * Upload Manager Store (Zustand) — the single owner of in-flight uploads and
 * connector import jobs for the global background-tasks tray.
 *
 * Two item types live side-by-side:
 *
 *  - `UploadItem`              — resumable file byte-transfer
 *  - `ConnectorImportPageItem` — one page being imported from a connector
 *
 * Surfaces (`AddAttachmentDialog`, `ImportFilesModal`, Bird) `enqueue()` their
 * files and close immediately; the store drives the resumable transport in the
 * background and the global `<UploadTray>` renders progress. The user navigates
 * the app freely while uploads run (the Notion pattern). The byte-transfer phase
 * is the only "keep the tab open" window — once an item is `done` (finalized),
 * server-side processing (transcode / extraction / notes) continues regardless.
 *
 * Connector-import pages are registered by `useConnectorImportTraySocket` (an
 * app-level subscription) when the discovery job emits
 * `connector-import:item-queued`. Their status is updated by
 * `connector-import:item-done` events.
 */

import type { UploadIntent as SharedUploadIntent } from '@bower/shared';
import { create } from 'zustand';
import { BACKGROUND_TASK_KIND } from '@bower/shared/constants/background-task';
import { CAPTURE_SURFACE_ONBOARDING_IMPORT } from '@bower/shared/constants/capture';
import { PROCESSING_STATUS } from '@bower/shared/constants/artifact-status';
import { SPREADSHEET_INTAKE_NOTE_MESSAGES } from '@bower/shared/constants/spreadsheet-intake';
import {
  getFileExtension,
  isAmbiguousMediaContainer,
  isAudioFile,
} from '@bower/shared/utils/fileTypeDetection';

import { performResumableUpload } from '../services/upload/performUpload';
import {
  TRANSCODE_TIMEOUT_MS,
  UploadReadinessError,
  waitForUploadReady,
} from '../services/upload/waitForUploadReady';
import { apiService } from '../services/api.service';
import { invalidateEntityQueries } from '../lib/queryClient';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from '../utils/analytics';
import { useEntityActivityStore } from './entityActivityStore';

// ---------------------------------------------------------------------------
// Upload items (byte-transfer)
// ---------------------------------------------------------------------------

/**
 * What the upload TRAY offers, which is a subset of what the server accepts.
 *
 * Derived from the shared vocabulary rather than restated, because the two had
 * already drifted: this file declared its own `UploadIntent` with two members
 * while `@bower/shared` grew a third, so one name meant two different sets
 * depending on the import. `source` is deliberately not offered here - it is
 * declared by a caller that owns the resulting artifact, not chosen in a tray.
 */
export type UploadIntent = Extract<SharedUploadIntent, 'attach' | 'import'>;
/**
 * `processing` is the server-side phase AFTER the bytes have landed: a video
 * finalises into `transcoding` and is not usable until the worker publishes the
 * playable MP4 (prod p90 ~54s, max ~152s). It used to be invisible — the row
 * went straight to `done` with a green check while the attachment did not yet
 * exist anywhere the user could see, which reads as a failed upload.
 */
export type UploadItemStatus = 'uploading' | 'processing' | 'done' | 'error';

export interface UploadItem {
  readonly kind: 'upload';
  readonly id: string;
  readonly fileName: string;
  readonly workspaceId: string;
  readonly parentEntityType: string;
  readonly parentEntityId: string;
  readonly intent: UploadIntent;
  readonly status: UploadItemStatus;
  /** 0–100. */
  readonly progress: number;
  readonly error?: string;
  readonly artifactId?: string;
  /** What the upload became ('attachment' unless finalize said otherwise);
   * picks the detail route a finished row links to. */
  readonly artifactType?: string;
  /** Server-side state returned by upload finalization. Separate from byte transfer. */
  readonly processingStatus?: string;
  /**
   * Set when we stopped watching a still-processing upload (wait deadline or an
   * unreachable status endpoint). NOT an error: the worker is still running and
   * the attachment will appear on its own. The tray says so rather than
   * implying failure.
   *
   * It also ends the row's claim on the tray. `status` stays `processing`
   * (nothing proved it finished, so a green check would be a lie), but every
   * "is there live work?" predicate treats it as finished — otherwise the tray
   * has no exit: `hasActive` stays true forever, which hides the Clear button,
   * pinning a full-width fixed overlay to a phone viewport permanently while
   * the heading claims work we explicitly stopped watching.
   */
  readonly stoppedWatching?: boolean;
  /** Shown under a `done` row: the file became a spreadsheet, or why it stayed a file. */
  readonly note?: string;
  /**
   * The note says why a spreadsheet stayed a file. The tray keeps such a row
   * until the user dismisses it (as it does an error), so the reason is not
   * gone before it can be read.
   */
  readonly keptAsFile?: boolean;
}

const SPREADSHEET_SETUP_NOTE = 'Uploaded. Setting up as a spreadsheet.';

/** The tray line for a finished upload, from the bounded intake code or the artifact type. */
function uploadNote(result: { artifactType?: string; intakeNote?: string }): string | undefined {
  if (result.intakeNote) {
    return Object.hasOwn(SPREADSHEET_INTAKE_NOTE_MESSAGES, result.intakeNote)
      ? SPREADSHEET_INTAKE_NOTE_MESSAGES[
          result.intakeNote as keyof typeof SPREADSHEET_INTAKE_NOTE_MESSAGES
        ]
      : undefined;
  }
  return result.artifactType === 'spreadsheet' ? SPREADSHEET_SETUP_NOTE : undefined;
}

export interface EnqueueContext {
  readonly workspaceId: string;
  readonly parentEntityType: string;
  readonly parentEntityId: string;
  readonly intent: UploadIntent;
  /** User-typed title — applied to single-file uploads only. */
  readonly title?: string;
  /** Lifecycle intent only — `ImportFilesModal` passes `onboarding_import`
   * through; the device is the `x-bower-client` header's job. */
  readonly captureSurface?: typeof CAPTURE_SURFACE_ONBOARDING_IMPORT;
  /** Note target for the Import flow ('protocol' for the Protocol Library);
   * only consulted server-side when intent is 'import'. */
  readonly targetArtifactType?: string;
  /** Request server-owned transcription for the audio files in this batch.
   * Only a note surface may set it — the note's onChildAttached is the sole
   * reader of the resulting sidecar flag; on any other parent it is inert.
   * Applied per file, and includes ambiguous containers (a `.webm` voice note
   * re-uploaded from disk arrives as `video/webm`): the transcode probe
   * reroutes an audio-only stream with its sidecar intact, and a real video
   * is dropped by the handler's processing-type gate. */
  readonly transcribeAudio?: boolean;
  /** Fired per file as it finalizes — lets a surface optimistically surface the
   * new attachment (e.g. the protocol page's "Processing…" tile). */
  readonly onUploaded?: (
    result: { artifactId: string; processingStatus: string },
    file: File,
  ) => void;
}

// ---------------------------------------------------------------------------
// Connector-import page items (server-side, progress via socket)
// ---------------------------------------------------------------------------

export type ConnectorImportPageStatus = 'pending' | 'done' | 'skipped' | 'error';

export interface ConnectorImportPageItem {
  readonly kind: 'connector-import-page';
  readonly id: string;
  /** Connector import run id — used to dismiss all pages for a run at once. */
  readonly runId: string;
  /** Human-readable page title (from the tree node name). */
  readonly pageName: string;
  readonly status: ConnectorImportPageStatus;
  readonly error?: string;
}

// ---------------------------------------------------------------------------
// Union tray item
// ---------------------------------------------------------------------------

export type TrayItem = UploadItem | ConnectorImportPageItem;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface UploadManagerState {
  readonly items: ReadonlyArray<TrayItem>;
  /** Enqueue file uploads (byte-transfer). */
  enqueue: (files: File[], ctx: EnqueueContext) => void;
  /** Cancel an in-flight upload. */
  cancel: (id: string) => void;
  /** Dismiss a finished item. */
  remove: (id: string) => void;
  /** Dismiss all finished items. */
  clearFinished: () => void;
  /** Register a connector-import page as pending (called when item-queued arrives). */
  registerImportPage: (item: Omit<ConnectorImportPageItem, 'kind'>) => void;
  /** Update a connector-import page's status (called when item-done arrives). */
  updateImportPage: (itemId: string, status: ConnectorImportPageStatus, error?: string) => void;
  /** Dismiss all tray rows belonging to a connector import run. */
  clearImportRun: (runId: string) => void;
}

// Per-upload abort handles + cancellation flags live outside the (serializable)
// store state.
const controllers = new Map<string, AbortController>();
const canceled = new Set<string>();

/**
 * Map an enqueue context to the invalidation `data` the queryClient helper uses
 * to refresh the right list shapes (workspace infinite-artifacts vs project
 * children). Import parents are always project/workspace; an attach-to-artifact
 * parent (Phase 5) has no list id here — recents/hierarchy/detail still refresh.
 */
function parentInvalidationData(
  ctx: EnqueueContext,
): { projectId?: string; workspaceId?: string } | undefined {
  if (ctx.parentEntityType === 'project') return { projectId: ctx.parentEntityId };
  if (ctx.parentEntityType === 'workspace') return { workspaceId: ctx.parentEntityId };
  return undefined;
}

/**
 * Is this upload still work in progress? Byte transfer, or a transcode we are
 * still following. A `stoppedWatching` row is deliberately NOT active: we gave
 * up on the wait, so it must stop holding the tray open (see `stoppedWatching`).
 */
export function isUploadActive(item: UploadItem): boolean {
  if (item.status === 'uploading') return true;
  return item.status === 'processing' && !item.stoppedWatching;
}

let counter = 0;
function nextId(): string {
  counter += 1;
  return `upload-${counter}-${counter * 31}`;
}

export const useUploadManagerStore = create<UploadManagerState>((set) => {
  // patchItem is only called from upload byte-transfer paths — it only patches UploadItems.
  const patchItem = (id: string, fields: Partial<UploadItem>) =>
    set((s) => ({
      items: s.items.map(
        (it): TrayItem => (it.id === id && it.kind === 'upload' ? { ...it, ...fields } : it),
      ),
    }));

  /**
   * Follow a finalized-but-still-transcoding upload to its terminal state.
   *
   * This wait is the ONLY thing that moves a tray row out of `processing`. It
   * does not poll: `waitForUploadReady` re-reads the status when a `/sync`
   * change names this upload (the transcode's READY/FAILED flip broadcasts) or
   * the sync room is rejoined, which is what recovers a flip broadcast while a
   * backgrounded phone's socket was down. The `upload:complete` push is not the
   * signal: it is a fire-and-forget emit with no persistence.
   *
   * Deliberately does NOT hold the item open forever: past the deadline we mark
   * it `stoppedWatching` and say the video will appear on its own, which is
   * true — the worker is server-side and outlives this tab.
   */
  const watchProcessing = (id: string, ctx: EnqueueContext, artifactId: string) => {
    // Re-register under the same id (the byte-transfer controller was cleared
    // when the transfer resolved) so dismissing or cancelling the row stops the
    // wait — otherwise a dismissed video keeps listening for ten minutes.
    const controller = new AbortController();
    controllers.set(id, controller);
    void waitForUploadReady(apiService, ctx.workspaceId, artifactId, {
      timeoutMs: TRANSCODE_TIMEOUT_MS,
      signal: controller.signal,
    })
      .then(() => {
        controllers.delete(id);
        if (canceled.has(id)) return;
        patchItem(id, { status: 'done', processingStatus: PROCESSING_STATUS.READY });
        // The READY flip's own /sync event refreshes the lists too; this names
        // the parent the event may not carry, so the placeholder is swapped
        // for the playable video either way.
        invalidateEntityQueries('attachment', 'updated', artifactId, parentInvalidationData(ctx));
      })
      .catch((err: unknown) => {
        controllers.delete(id);
        if (canceled.has(id)) return;
        const code = err instanceof UploadReadinessError ? err.code : 'TRANSPORT_ERROR';
        // Dismissed / cancelled: the row is gone, and the worker carries on
        // server-side. Nothing to report.
        if (code === 'CANCELED') return;
        if (code === 'FAILED' || code === 'UPLOAD_NOT_FOUND') {
          patchItem(id, {
            status: 'error',
            processingStatus: PROCESSING_STATUS.FAILED,
            // Media-neutral, and no "try again": a phone labels a voice memo
            // `video/webm`, and the detail page deliberately offers download
            // rather than retry because these rejections are deterministic.
            // Two user-facing accounts of one failure must not disagree on
            // either the medium or the remedy.
            error: "This file couldn't be processed. Open it to download your original.",
          });
          // Swap the optimistic placeholder for the server's failed tile: browse
          // readers now RETURN a failed row (it carries the recovery affordance),
          // so this refetch replaces what is on screen rather than removing it.
          invalidateEntityQueries('attachment', 'updated', artifactId, parentInvalidationData(ctx));
          return;
        }
        // TIMEOUT / TRANSPORT_ERROR / WORKSPACE_LEFT / anything else: we stopped watching, the
        // server did not stop working. Never render that as a failure.
        patchItem(id, { stoppedWatching: true });
      });
  };

  const drive = (id: string, file: File, ctx: EnqueueContext) => {
    const controller = new AbortController();
    controllers.set(id, controller);
    void performResumableUpload(
      file,
      {
        workspaceId: ctx.workspaceId,
        parentEntityType: ctx.parentEntityType,
        parentEntityId: ctx.parentEntityId,
        importAsNotes: ctx.intent === 'import',
        title: ctx.title,
        captureSurface: ctx.captureSurface,
        targetArtifactType: ctx.targetArtifactType,
        ...(ctx.transcribeAudio &&
        (isAudioFile(file.name, file.type) || isAmbiguousMediaContainer(file.name, file.type))
          ? { serverTranscription: true }
          : {}),
      },
      (pct) => {
        if (!canceled.has(id)) patchItem(id, { progress: pct });
      },
      controller.signal,
    )
      .then((result) => {
        controllers.delete(id);
        // A canceled item is already gone from `items`; clear its flag so the
        // module-level Set can't grow unbounded over a long session.
        if (canceled.has(id)) {
          canceled.delete(id);
          return;
        }
        // A video finalises into `transcoding`, not `ready` — the bytes are safe
        // but the playable MP4 does not exist yet. Reporting that as `done`
        // (green check, auto-hide after 3s) was the core of the "did my upload
        // fail?" report: we claimed success, then the attachment was nowhere.
        const stillProcessing = result.processingStatus === PROCESSING_STATUS.TRANSCODING;
        const isSpreadsheet = result.artifactType === 'spreadsheet';
        const note = uploadNote(result);
        patchItem(id, {
          status: stillProcessing ? 'processing' : 'done',
          progress: 100,
          artifactId: result.artifactId,
          // Finalize returns the row's type; resumable uploads are only ever
          // attachments or spreadsheets, so an absent field means attachment.
          artifactType: result.artifactType ?? 'attachment',
          processingStatus: result.processingStatus,
          note,
          keptAsFile: Boolean(result.intakeNote && note),
        });
        if (isSpreadsheet) {
          // The import job runs next; show it as queued before its first /sync
          // event. seedTask (not setTask): if the completion event already
          // arrived, this stale "queued" seed must not overwrite it.
          useEntityActivityStore.getState().seedTask(result.artifactId, {
            kind: BACKGROUND_TASK_KIND.SPREADSHEET_IMPORT,
            status: 'queued',
            slotKey: BACKGROUND_TASK_KIND.SPREADSHEET_IMPORT,
          });
          safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_IMPORTED, {
            format: getFileExtension(file.name),
            in_collection: ctx.parentEntityType === 'project',
            source: 'upload',
          });
        }
        // The surface that started this upload has usually closed (async-first),
        // so refresh the lists that hide in-flight rows here — otherwise the
        // finished attachment wouldn't appear until a manual reload. (The READY
        // flip is a conditional `updateMany`, which the audit/broadcast Prisma
        // extension doesn't hook, so no server sync event fires for it.)
        // Debounced + batched in queryClient → a multi-file import coalesces
        // into a couple of refetches.
        //
        // Fired for a `transcoding` result too: browse readers now RETURN that
        // row (BROWSE_HIDDEN_PROCESSING_STATUSES), so this is what puts the
        // "Processing…" placeholder on screen instead of nothing at all.
        invalidateEntityQueries(
          isSpreadsheet ? 'spreadsheet' : 'attachment',
          'created',
          result.artifactId,
          parentInvalidationData(ctx),
        );
        ctx.onUploaded?.(result, file);
        if (stillProcessing) watchProcessing(id, ctx, result.artifactId);
      })
      .catch((err: unknown) => {
        controllers.delete(id);
        if (canceled.has(id)) {
          canceled.delete(id); // an intentional cancel isn't an error; clear the flag
          return;
        }
        patchItem(id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      });
  };

  return {
    items: [],

    enqueue: (files, ctx) => {
      const single = files.length === 1;
      const newItems: UploadItem[] = files.map((file) => ({
        kind: 'upload' as const,
        id: nextId(),
        fileName: file.name,
        workspaceId: ctx.workspaceId,
        parentEntityType: ctx.parentEntityType,
        parentEntityId: ctx.parentEntityId,
        intent: ctx.intent,
        status: 'uploading' as const,
        progress: 0,
      }));
      set((s) => ({ items: [...s.items, ...newItems] }));
      newItems.forEach((item, i) =>
        drive(item.id, files[i], { ...ctx, title: single ? ctx.title : undefined }),
      );
    },

    cancel: (id) => {
      canceled.add(id);
      controllers.get(id)?.abort();
      controllers.delete(id);
      set((s) => ({ items: s.items.filter((it) => it.id !== id) }));
    },

    remove: (id) => {
      // Abort first: a `processing` row still owns a live readiness wait.
      controllers.get(id)?.abort();
      controllers.delete(id);
      canceled.delete(id);
      set((s) => ({ items: s.items.filter((it) => it.id !== id) }));
    },

    clearFinished: () =>
      set((s) => ({
        items: s.items.filter((it) =>
          it.kind === 'upload' ? isUploadActive(it) : it.status === 'pending',
        ),
      })),

    registerImportPage: (item) =>
      set((s) => {
        // Idempotent: if the item already exists, don't duplicate.
        if (s.items.some((it) => it.id === item.id)) return s;
        const newItem: ConnectorImportPageItem = { kind: 'connector-import-page', ...item };
        return { items: [...s.items, newItem] };
      }),

    updateImportPage: (itemId, status, error) =>
      set((s) => ({
        items: s.items.map((it) =>
          it.id === itemId && it.kind === 'connector-import-page'
            ? { ...it, status, ...(error !== undefined ? { error } : {}) }
            : it,
        ),
      })),

    clearImportRun: (runId) =>
      set((s) => ({
        items: s.items.filter((it) => !(it.kind === 'connector-import-page' && it.runId === runId)),
      })),
  };
});

/** Selector: any upload still transferring bytes (the keep-tab-open window). */
export function selectHasActiveUploads(s: UploadManagerState): boolean {
  return s.items.some((it) => it.kind === 'upload' && it.status === 'uploading');
}

/** Selector: any item (upload or connector-import page) still in-flight.
 *
 * Includes `processing`: a transcoding video is still work in progress, so the
 * tray must not offer to Clear it the way it does a finished upload. (The wake-lock selector above deliberately does NOT include it —
 * that phase is server-side and survives a sleeping tab.) */
export function selectHasActiveItems(s: UploadManagerState): boolean {
  return s.items.some((it) =>
    it.kind === 'upload' ? isUploadActive(it) : it.status === 'pending',
  );
}
